import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { memo } from "react";
import { useWhatsAppStatus } from "../../hooks/api/whatsappApiHooks";
import { formatDateHourMinuteSecond } from "../../services/date";

/**
 * Indicador de status WhatsApp para navbar
 * Seguindo padrões de componentes shared do projeto JBE Engenharia
 */

const WhatsAppStatusIndicator = memo(() => {
  const theme = useTheme();
  const { whatsappStatus, whatsappStatusLoading, whatsappStatusError } = useWhatsAppStatus();
  
  // Lógica de estados visuais baseada na especificação
  const getStatusConfig = () => {
    // Se API falhou: mostrar indicador cinza
    if (whatsappStatusError) {
      return {
        color: theme.palette.grey[500],
        message: "Status indisponível",
        status: "error",
        iconColor: theme.palette.grey[500]
      };
    }

    // Se ainda está carregando: usar amarelo
    if (whatsappStatusLoading) {
      return {
        color: theme.palette.warning.main,
        message: "Verificando conexão...",
        status: "loading",
        iconColor: theme.palette.warning.main
      };
    }

    // Se não há dados: vermelho
    if (!whatsappStatus || whatsappStatus.length === 0) { 
      return {
        color: theme.palette.error.main,
        message: "Nenhuma conexão configurada",
        status: "no_connections",
        iconColor: theme.palette.error.main
      };
    }

    // Verificar status das conexões
    const hasOpenConnection = whatsappStatus.some((conn) => conn.status === "open");
    const hasConnectingConnection = whatsappStatus.some(
      (conn) => conn.status === "connecting"
    );
    const allClosed = whatsappStatus.every(
      (conn) => conn.status === "close" || conn.status === "failed"
    );

    if (hasOpenConnection) {
      return {
        color: theme.palette.success.main,
        message: "Conectado",
        status: "connected",
        iconColor: theme.palette.success.main
      };
    }

    if (hasConnectingConnection) {
      return {
        color: theme.palette.warning.main,
        message: "Conectando...",
        status: "connecting",
        iconColor: theme.palette.warning.main
      };
    }

    if (allClosed) {
      return {
        color: theme.palette.error.main,
        message: "Desconectado",
        status: "disconnected",
        iconColor: theme.palette.error.main
      };
    }

    // Estado padrão
    return {
      color: theme.palette.grey[500],
      message: "Status desconhecido",
      status: "unknown",
      iconColor: theme.palette.grey[500]
    };
  };

  const statusConfig = getStatusConfig();

  // Componente React para tooltip estruturado
  const TooltipContent = () => {
    const currentTime = new Date();
    const formattedCurrentTime = formatDateHourMinuteSecond(currentTime);

    return (
      <Box sx={{ p: 1, maxWidth: 280 }}>
        {/* Status Principal */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          🔹 WhatsApp Status: 
          <Typography 
            component="span" 
            sx={{ 
              color: statusConfig.color, 
              fontWeight: 'bold',
              ml: 0.5 
            }}
          >
            {statusConfig.message}
          </Typography>
        </Typography>

        {/* Atualização Automática */}
        <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          🔄 Atualização automática: a cada 30 segundos
        </Typography>

        {/* Última Verificação */}
        <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          📅 Verificado em: {formattedCurrentTime}
        </Typography>

        {/* Erro (se houver) */}
        {whatsappStatusError && (
          <Typography variant="body2" sx={{ color: theme.palette.error.main, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            ⚠️ Erro: Não foi possível verificar o status
          </Typography>
        )}

        {/* Estado de Loading */}
        {whatsappStatusLoading && (
          <Typography variant="body2" sx={{ color: theme.palette.info.main, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            ⏳ Verificando status...
          </Typography>
        )}
      </Box>
    );
  };

  const containerSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.8,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "16px",
    backgroundColor: theme.palette.mode === "light" 
      ? "rgba(255, 255, 255, 0.1)" 
      : "rgba(0, 0, 0, 0.1)",
    transition: theme.transitions.create(["background-color", "transform"], {
      duration: theme.transitions.duration.standard,
    }),
    '&:hover': {
      backgroundColor: theme.palette.mode === "light" 
        ? "rgba(255, 255, 255, 0.2)" 
        : "rgba(0, 0, 0, 0.2)",
      transform: 'scale(1.05)',
    }
  };

  const indicatorSx = {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: statusConfig.color,
    animation: statusConfig.status === "connecting" ? "pulse 2s infinite" : "none",
    "@keyframes pulse": {
      "0%": {
        opacity: 1,
      },
      "50%": {
        opacity: 0.5,
      },
      "100%": {
        opacity: 1,
      },
    }
  };

  const iconSx = {
    fontSize: "16px",
    color: statusConfig.iconColor,
  };

  const textSx = {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.common.white,
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
  };

  return (
    <Tooltip 
      title={<TooltipContent />}
      placement="bottom-end"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: theme.palette.grey[900],
            color: 'white',
            fontSize: '0.875rem',
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            '& .MuiTooltip-arrow': {
              color: theme.palette.grey[900],
            },
          }
        }
      }}
    >
      <Box sx={containerSx}>
        <WhatsAppIcon sx={iconSx} />
        <Box sx={indicatorSx} />
        <Typography sx={textSx}>
          WhatsApp
        </Typography>
      </Box>
    </Tooltip>
  );
});

WhatsAppStatusIndicator.displayName = "WhatsAppStatusIndicator";

export default WhatsAppStatusIndicator;
